import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Box,
  Typography,
  Slide,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { addSparePart, updateSparePart } from "../../../Services/authService";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../Components/Common/StyledTextField";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function SapresModal({
  open,
  onClose,
  selectedProduct,
  refreshList,
  handleDeleteClick,
}) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const modalBackgrounColor =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const imgBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale60 : LIGHTCOLORS.GreyScale60;
  const deleteColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

  const [formData, setFormData] = useState({
    productName: "",
    categoryType: "",
    description: "",
    price: "",
    availableQuantity: "",
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null); // File object for the image
  const [generalError, setGeneralError] = useState("");
  const [successNotification, setSuccessNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [open1, setOpen1] = useState(false);

  // Reset every field back to its initial state (used on close and after save)
  const resetForm = () => {
    setFormData({
      productName: "",
      categoryType: "",
      description: "",
      price: "",
      availableQuantity: "",
    });
    setErrors({});
    setGeneralError("");
    setImagePreview(null);
    setImageFile(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    // Only (re)populate while open; keying on `open` too means reopening the
    // same product refills the form after a close-time reset.
    if (!open) return;
    setGeneralError("");
    setErrors({});
    if (selectedProduct) {
      setFormData({
        productName: selectedProduct.productName,
        categoryType: selectedProduct.categoryType,
        description: selectedProduct.description,
        price: selectedProduct.price,
        availableQuantity: selectedProduct.availableQuantity,
      });
      setImagePreview(selectedProduct.imageUrl || null);
    } else {
      setFormData({
        productName: "",
        categoryType: "",
        description: "",
        price: "",
        availableQuantity: "",
      });
      setImagePreview(null);
    }
  }, [selectedProduct, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.productName)
      newErrors.productName = t("inventory.Productnamerequired");
    if (!formData.categoryType)
      newErrors.categoryType = t("inventory.Categoryisrequired");
    if (!formData.availableQuantity) {
      newErrors.availableQuantity = t("spare.quantityRequired");
    } else if (
      isNaN(formData.availableQuantity) ||
      Number(formData.availableQuantity) <= 0
    ) {
      newErrors.availableQuantity = t("spare.quantityMustBePositive");
    }

    if (!formData.price) {
      newErrors.price = t("inventory.Priceisrequired");
    } else if (isNaN(formData.price) || Number(formData.price) <= 0) {
      newErrors.price = t("inventory.Pricemustpositive");
    }
    if (!formData.description)
      newErrors.description = t("inventory.Descriptionisrequired");

    setErrors(newErrors);
    setGeneralError(
      Object.keys(newErrors)?.length > 0
        ? t("inventory.Pleasefillrequired")
        : "",
    );
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append("data", JSON.stringify(formData));
      if (imageFile) formDataToSend.append("imageFile", imageFile);

      setIsLoading(true);
      if (selectedProduct) {
        // Update the product
        await updateSparePart(selectedProduct.id, formDataToSend);
      } else {
        // Add a new product
        await addSparePart(formDataToSend);
      }
      setSuccessNotification(true);
      onClose();
      refreshList();
      // Reset the form
      resetForm();
    } catch (error) {
      const detail = error?.response?.data?.detail;

      if (detail?.includes("already exists")) {
        setGeneralError(t("spare.sparePartAlreadyExists"));
      } else {
        setGeneralError(detail || t("spare.errorSavingProduct"));
      }

      console.error(t("spare.errorSavingProduct"), error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Image Selection
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      if (!validImageTypes.includes(file.type)) {
        setErrors({ ...errors, image: t("inventory.Pleasevalidimage") });
        return;
      }
      // Validate file size (limit to 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        setErrors({ ...errors, image: t("spare.fileSizeExceeded") });
        setToastMessage(t("spare.fileSizeExceeded"));
        setOpen1(true); // Show toast
        return;
      }

      // Generate image preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result); // Set the image preview
        setErrors({ ...errors, image: "" }); // Clear image errors
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  return (
    <>
      {isLoading && <LoadingAnimation />}
      <Dialog
        className="inventory-product-sec"
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "absolute",
            right: 10,
            margin: 0,
            // height: '100vh', // Full height
            borderRadius: 3,
            background: modalBackgrounColor,
          },

          "@media (max-width: 427px)": {
            right: 5,
            width: "97% !important",
          },
        }}
      >
        {/* Title with Close Icon */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
          }}
        >
          <span className="franchise-heading-text">
            {selectedProduct
              ? t("inventory.editProduct")
              : t("inventory.addProduct")}
          </span>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            paddingTop: 2,
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
          }}
        >
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Image Upload */}
            <Box
              sx={{
                border: `1px dashed ${imgBoxColor}`,
                borderRadius: "8px",
                textAlign: "center",
                cursor: "pointer",
                position: "relative",
                width: "100%",
                height: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
              onClick={() => document.getElementById("upload-image").click()}
            >
              <input
                type="file"
                id="upload-image"
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageUpload}
              />
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: "100%",
                    // maxHeight: '100%',
                    // objectFit: 'contain',
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <div>
                  <AddPhotoAlternateIcon
                    sx={{
                      fontSize: "8rem",
                      color: "#C6C6C6",
                    }}
                  />
                  <Typography sx={{ color: imgBoxColor }}>
                    {t("inventory.uploadImage")}
                    <br />
                    {t("inventory.uploadImage1")} 500×320 px
                    <br />
                    {t("inventory.uploadImage2")} (1000×640 px)
                  </Typography>
                </div>
              )}
            </Box>

            {/* Name Fields */}
            <Box>
              <StyledTextField
                label={`${t("inventory.nameoftheProduct")}`}
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                error={errors.productName}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Available quantity */}
            <Box>
              <StyledTextField
                label={`${t("spare.availableQuanitity")}`}
                name="availableQuantity"
                numberType="true"
                inputProps={{ min: 1 }}
                value={formData.availableQuantity}
                onChange={handleChange}
                error={errors.availableQuantity}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Select Category */}
            <Box>
              <StyledTextField
                label={`${t("inventory.selectCategory")}`}
                name="categoryType"
                value={formData.categoryType}
                onChange={handleChange}
                error={errors.categoryType}
                inputBoxColor={inputBoxColor}
                required
              >
                <MenuItem value="Gourmet">{t("spare.GourmetMachine")}</MenuItem>
                <MenuItem value="Pizza">{t("spare.PizzaMachine")}</MenuItem>
                <MenuItem value="Return">{t("spare.ReturnMachine")}</MenuItem>
                <MenuItem value="Snack">{t("vendingMachines.Snack")}</MenuItem>
                {/* <MenuItem value="French Fries">{t("spare.FrenchFriesMachine")}</MenuItem> */}
                {/* <MenuItem value="Ice Cream">{t("spare.IceCreamMachine")}</MenuItem> */}
                {/* <MenuItem value="Candy">{t("spare.CandyMachine")}</MenuItem> */}
                {/* <MenuItem value="Beer">{t("spare.BeerMachine")}</MenuItem> */}
              </StyledTextField>
            </Box>

            {/* Price */}
            <Box>
              <StyledTextField
                label={`${t("inventory.priceoftheProduct")} (€)`}
                name="price"
                numberType="true"
                inputProps={{ min: 1 }}
                value={formData.price}
                onChange={handleChange}
                error={errors.price}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Description */}
            <Box>
              <TextField
                label={`${t("inventory.description")}`}
                name="description"
                value={formData.description}
                onChange={handleChange}
                error={errors.description}
                type="text"
                variant={"filled"}
                InputProps={{ disableUnderline: true }}
                multiline
                rows={2}
                fullWidth
                sx={{
                  backgroundColor: inputBoxColor,
                  borderRadius: "6px",
                  "& .MuiFilledInput-root": {
                    backgroundColor: inputBoxColor,
                    borderRadius: "6px",
                    "&:hover": { backgroundColor: inputBoxColor },
                    "&.Mui-focused": { backgroundColor: inputBoxColor },
                  },
                }}
                required
              />
            </Box>

            {generalError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: "center", marginTop: 1 }}
              >
                {generalError}
              </Typography>
            )}

            {/* Action Buttons */}
            {selectedProduct ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <button
                  type="button"
                  style={{ color: deleteColor, borderColor: deleteColor }}
                  className="cancel-btn"
                  onClick={handleDeleteClick}
                >
                  {t("membershipCards.delete")}
                </button>
                <Box style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    style={{ color: btnColor, borderColor: btnColor }}
                    className="cancel-btn"
                    onClick={handleClose}
                  >
                    {t("membershipCards.cancel")}
                  </button>
                  <button
                    type="button"
                    style={{ background: btnColor }}
                    className="save-btn"
                    onClick={handleSave}
                  >
                    {t("membershipCards.save")}
                  </button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <button
                  type="button"
                  style={{ color: btnColor, borderColor: btnColor }}
                  className="cancel-btn"
                  onClick={handleClose}
                >
                  {t("membershipCards.cancel")}
                </button>
                <button
                  type="button"
                  style={{ background: btnColor }}
                  className="save-btn"
                  onClick={handleSave}
                >
                  {t("membershipCards.save")}
                </button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={t(
          selectedProduct
            ? "membershipCards.successMessage"
            : "inventory.productSuccessMessage",
        )}
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />

      {/* // Snackbar component */}
      <Snackbar
        open={open1}
        autoHideDuration={4000} // Auto hide after 3 seconds
        onClose={() => setOpen1(false)} // Close the Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Position the Snackbar at the top center
      >
        <Alert
          onClose={() => setOpen1(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SapresModal;
